import { link } from 'fs'
import { Box, Link, Typography } from '@mui/material'
import { appPaths } from '@config'
import { Icon, Image, ImageData } from '@media-components'
import { ActionButton } from '@components/common'
import { publicImages } from '@utils/shared'
import { CustomStyles, getStyles } from '@utils/styles'
import defaultStyles, { StylesClassNames } from './styles'

export type AuthorCardData = {
  authorDescription?: string
  authorDesignation?: string
  authorName: string
  authorFacebookUrl: string
  authorSlug: string
  authorImage?: ImageData
  authorInstagramUrl?: string
  authorTwitterUrl?: string
  authorLinkedIn?: string
  emailAddress?: string
  isVerified?: boolean
  numberOfArticles?: number
  darkBg?: boolean
}
type Props = AuthorCardData & {
  csx?: CustomStyles<StylesClassNames>
}
export function AuthorCard({
  authorDescription,
  authorDesignation,
  authorImage,
  authorName,
  authorFacebookUrl,
  authorInstagramUrl,
  authorTwitterUrl,
  authorLinkedIn,
  numberOfArticles,
  isVerified = true,
  authorSlug,
  csx,
  darkBg = false,
}: Props) {
  const styles = getStyles(defaultStyles, csx)

  const socials = [
    {
      icon: publicImages.facebookNew,
      link: authorFacebookUrl || '',
    },
    {
      icon: publicImages.instaNew,
      link: authorInstagramUrl || '',
    },
    {
      icon: publicImages.linkedinNew,
      link: authorLinkedIn || '',
    },
    {
      icon: publicImages.twitterIcon,
      link: authorTwitterUrl || '',
    },
  ].filter((i) => i.link)

  if (!authorDescription) return null
  const authorProfileUrl = `${appPaths.authors}/${authorSlug}`

  return (
    <Box
      {...styles('cardWrapper', {
        cursor: authorProfileUrl ? 'pointer' : 'default',
        backgroundColor: darkBg ? 'custom.darkGreyIsh' : 'background.paper',
      })}
      {...(authorProfileUrl ? { component: Link, href: authorProfileUrl } : {})}
    >
      <Box display="flex" gap="16px" flexDirection="column">
        <Typography
          variant="caption2.600"
          color={darkBg ? 'text.primaryDark' : 'text.primary'}
        >
          The Author
        </Typography>
        <Box display="flex" flexDirection="column" gap="8px">
          <Box {...styles('authorHeader')}>
            <Box display="flex" gap="4px">
              <Typography
                variant="body1.700"
                {...styles('authorName')}
                color={darkBg ? 'text.primaryDark' : 'text.primary'}
              >
                {authorName}
              </Typography>
              {isVerified && (
                <Icon
                  iconName="Verified"
                  currentValue={false}
                  fontSize="medium"
                />
              )}
            </Box>

            <Typography color={darkBg ? 'text.primaryDark' : 'text.primary'}>
              •
            </Typography>
            {authorDesignation && (
              <Typography
                variant="caption1.500"
                color={darkBg ? 'text.primaryDark' : 'text.primary'}
              >
                {authorDesignation}
              </Typography>
            )}
            <Typography color={darkBg ? 'text.primaryDark' : 'text.primary'}>
              •
            </Typography>
            <Typography
              variant="caption1.500"
              color={darkBg ? 'text.primaryDark' : 'text.primary'}
            >
              {numberOfArticles} Articles
            </Typography>
          </Box>
          {authorDescription && (
            <Typography
              variant="body3.400"
              {...styles('authorDescription')}
              color={darkBg ? 'text.primaryDark' : 'text.primary'}
            >
              {authorDescription}
            </Typography>
          )}
          <Box display="flex" gap="8px">
            <Typography
              variant="caption1.500"
              color={darkBg ? 'text.primaryDark' : 'text.primary'}
            >
              Follow:
            </Typography>
            <Box display="flex" gap="8px">
              {socials.map((i) => (
                <Image
                  key={i.icon}
                  {...styles('socialImage', {
                    backgroundColor: darkBg ? '' : 'text.secondary',
                  })}
                  alt={i.link || ''}
                  width={16}
                  height={16}
                  src={i.icon}
                />
              ))}
            </Box>
          </Box>
        </Box>
        <ActionButton
          {...styles('profileButton')}
          variant="text"
          showArrow
          link={`${appPaths.authors}/${authorSlug}`}
        >
          View Profile
        </ActionButton>
      </Box>
      {authorImage && (
        <Image
          src={authorImage.src}
          alt={authorImage.alt}
          fullWidth
          {...styles('authorImage')}
        />
      )}
    </Box>
  )
}
